import { User } from '@adsk/alloy-provider';

import {
  ItemWithGroup,
  StringValueKeys,
  UserByGroupMap,
  UserGroupMap,
} from '../types';

/**
 * Creates helper maps to be used when counting the selected users for when includeMembers is true
 * @param userOptionValueGetter the preferred id field name
 * @param users the users
 * @returns the helper maps used by getTotalSelectedCount to compute the count
 */
export function createHelperMappings(
  userOptionValueGetter: StringValueKeys<User>,
  users: User[],
) {
  // Needed to prevent counting users again when they're in two or more selected groups
  const usersByCompany: UserByGroupMap = {};
  const usersByRole: UserByGroupMap = {};

  // Needed to lookup what company or role the user belongs to when they get selected, we don't want to modify the item object that goes into the dropdown option
  const userRoles: UserGroupMap = {};
  const userCompanies: UserGroupMap = {};

  // Groups the users by companies and roles
  users.forEach((user) => {
    const userId = user[userOptionValueGetter];

    if (user.companyGroupId) {
      const companyId = user.companyGroupId;

      if (!usersByCompany[companyId]) {
        usersByCompany[companyId] = {};
      }

      usersByCompany[companyId][userId] = userId;

      if (!userCompanies[userId]) {
        userCompanies[userId] = [];
      }

      userCompanies[userId] = [...userCompanies[userId], companyId];
    }

    user.roles?.forEach(({ roleGroupId: roleId }) => {
      if (!usersByRole[roleId]) {
        usersByRole[roleId] = {};
      }

      usersByRole[roleId][userId] = userId;

      if (!userRoles[userId]) {
        userRoles[userId] = [];
      }

      userRoles[userId] = [...userRoles[userId], roleId];
    });
  });

  return { usersByCompany, usersByRole, userCompanies, userRoles };
}

/**
 * Computes total selected count accounting for when the user wants to include the members in a group,
 * in which case, the members will be included in the sum for the group
 *
 * @param includeMembers if we're including the members of the groups
 * @param selectedItems the items currently selected by the user
 * @param userCompanies a map of all the companies each user belongs to (key is user id)
 * @param userRoles a map of all the roles each user belongs to (key is user id)
 * @param usersByCompany a map of all the users from a company (key is company id)
 * @param usersByRole a map of all the users from a role (key is role id)
 * @returns the total count which would include the members when includeMembers is true
 */
export function getTotalSelectedCount(
  includeMembers: boolean,
  selectedItems: ItemWithGroup[],
  userCompanies: UserGroupMap,
  userRoles: UserGroupMap,
  usersByCompany: UserByGroupMap,
  usersByRole: UserByGroupMap,
) {
  // If we're not including the members, then count will be just what the user selected
  if (!includeMembers) {
    return selectedItems?.length || 0;
  }

  const unassignedUsers: string[] = [];
  const selectedCompanies: { [id: string]: string } = {};
  const selectedRoles: { [id: string]: string } = {};
  const selectedUsers: { [id: string]: string } = {};

  // First records all the companies, roles, unassigned users or users that got selected
  selectedItems?.forEach((item) => {
    const itemId = item.value;

    if (item.group === 'company') {
      selectedCompanies[itemId] = itemId;
      return;
    }

    if (item.group === 'role') {
      selectedRoles[itemId] = itemId;
      return;
    }

    if (!userCompanies[itemId] && !userRoles[itemId]) {
      // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator array
      unassignedUsers.push(item.value);
      return;
    }

    selectedUsers[item.value] = item.value;
  });

  // These two  would automatically prevent duplicated users, since the key is user id

  // Records all users from companies that got selected
  Object.keys(selectedCompanies).forEach((companyId) => {
    /* NOTE: We get better performance by letting usersByCompany[companyId] be undefined if company has no members
     * than somehow assigning {} to all companies that have no members in this map */
    Object.keys(usersByCompany[companyId] || {}).forEach(
      (userId) => (selectedUsers[userId] = userId),
    );
  });

  // Records all users from roles that got selected
  Object.keys(selectedRoles).forEach((roleId) => {
    /* NOTE: We get better performance by letting usersByRole[roleId] be undefined if role has no members
     * than somehow assigning {} to all roles that have no members in this map */
    Object.keys(usersByRole[roleId] || {}).forEach(
      (userId) => (selectedUsers[userId] = userId),
    );
  });

  const totalSelectedCount =
    unassignedUsers.length +
    Object.keys(selectedCompanies).length + // +1 for each group selected
    Object.keys(selectedRoles).length + // +1 for each group selected
    Object.keys(selectedUsers).length; // All group users or single user selections would be included here

  return totalSelectedCount;
}
